import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from './apiClient';
import './auth.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (pw.length >= 12) score += 1;
  return score;
}
const STRENGTH_LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
const STRENGTH_COLORS = ['#dc2626', '#ef4444', '#f59e0b', '#eab308', '#10b981', '#16a34a'];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const score = useMemo(() => passwordStrength(password), [password]);
  const meetsPolicy = (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );

  if (!token) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-brand"><span className="auth-brand-dot"></span> Estatel AI</div>
          <h1 className="auth-title">Invalid link</h1>
          <p className="auth-subtitle">This reset link is missing or malformed.</p>
          <div className="auth-footer">
            <Link to="/forgot-password">Request a new reset link</Link>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!meetsPolicy) {
      setError('Password must be at least 8 characters and contain an uppercase letter, a number, and a special character.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await apiRequest('POST', `${BACKEND_URL}/api/auth/reset-password`, {
        body: { token, new_password: password, confirm_password: confirm },
      });
      if (!ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Reset failed.');
        return;
      }
      navigate('/login?msg=' + encodeURIComponent(data.message || 'Password reset successfully. Please log in with your new password.'), { replace: true });
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="reset-card">
        <div className="auth-brand"><span className="auth-brand-dot"></span> Estatel AI</div>
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-subtitle">Choose a strong new password for your account.</p>
        {error && <div className="auth-error" data-testid="reset-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="auth-field">
            <label htmlFor="reset-password">New password</label>
            <input id="reset-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" data-testid="reset-password" />
            {password && (
              <div className="auth-strength">
                <div className="auth-strength-bar">
                  <div className="auth-strength-fill" style={{ width: `${(score / 5) * 100}%`, background: STRENGTH_COLORS[score] }} />
                </div>
                <div style={{ color: STRENGTH_COLORS[score], fontWeight: 500 }}>{STRENGTH_LABELS[score]}</div>
              </div>
            )}
          </div>
          <div className="auth-field">
            <label htmlFor="reset-confirm">Confirm new password</label>
            <input id="reset-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required autoComplete="new-password" data-testid="reset-confirm" />
          </div>
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', background: submitting ? '#94a3b8' : '#0073EA', color: '#fff',
              border: 'none', borderRadius: 6, padding: '0.7rem', fontSize: '0.95rem',
              fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer', marginTop: '0.5rem',
            }}
            data-testid="reset-submit"
          >
            {submitting ? 'Saving…' : 'Reset password'}
          </button>
        </form>
        <div className="auth-footer">
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
